<template>
    <div>
        <b-row
            :class="[!element.selected ? 'font-italic' : '']"
            class="w-100 pt-3 ml-0"
            align-v="start"
            align-h="between"
        >
            <b-col class="pl-0" cols="8">
                <b-form-checkbox
                    v-model="element.selected"
                    class="align-items-start"
                    plain
                    :disabled="changeDateTime"
                    @change="stateChanged"
                >
                    <div v-if="isConvertEstimate">
                        <b>{{ element.patient.name }}</b>
                    </div>
                    <span v-if="element.medications">
                        {{
                            _.map(
                                element.medications,
                                medication => medication.name
                            ).join(",")
                        }}
                        {{ displayQuantity }}
                    </span>
                    <span v-if="element.otherTasks">
                        {{
                            _.map(
                                element.otherTasks,
                                task => task.taskType
                            ).join(",")
                        }}
                    </span>
                    <span v-if="element.vitals">
                        {{
                            _.map(element.vitals, vital => vital.name).join(
                                ", "
                            )
                        }}
                    </span>
                    <div>
                        <a
                            v-if="!changeDateTime"
                            href="javascript:void(0)"
                            @click="changeDateTime = true"
                            >{{ due_text }}
                            <i
                                class="fas fa-pen btn-inline-edit secondary"
                                style="margin-left: 2px;"
                            ></i>
                        </a>
                        <template v-else>
                            <!-- one-liner -->
                            <div class="d-flex align-items-center">
                                <!-- dropdown grows to fill the remaining width -->
                                <b-form-select
                                    id="dueType"
                                    ref="dueType"
                                    v-model="dueType"
                                    :options="dues"
                                    name="dueType"
                                    class="form-control flex-grow-1 mr-2"
                                />

                                <!-- icons -->
                                <span
                                    class="custom-button mr-2 cursor-pointer"
                                    @click.prevent="updateDateTime"
                                >
                                    <i class="fas fa-check"></i>
                                </span>
                                <span
                                    class="custom-button mr-1 cursor-pointer"
                                    @click.prevent="changeDateTime = false"
                                >
                                    <i class="fas fa-times"></i>
                                </span>
                            </div>
                            <div
                                v-if="dueType === AFTERSTARTTREATMENT"
                                class="d-flex flex-row mt-1"
                                style="flex: 30%;"
                            >
                                <div class="d-flex flex-column mr-2">
                                    <b-form-input
                                        id="dueAfterStartTreatmentDay"
                                        ref="dueAfterStartTreatmentDay"
                                        v-model.number="dueInDays"
                                        v-validate="'numeric'"
                                        type="number"
                                        name="dueAfterStartTreatmentDay"
                                        class="form-control"
                                        placeholder="Optional"
                                    ></b-form-input>
                                    <label class="ml-1">Day(s)</label>
                                </div>

                                <div class="d-flex flex-column mr-2">
                                    <b-form-input
                                        id="dueAfterStartTreatmentHour"
                                        ref="dueAfterStartTreatmentHour"
                                        v-model.number="dueInHours"
                                        v-validate="'numeric'"
                                        type="number"
                                        name="dueAfterStartTreatmentHour"
                                        class="form-control"
                                        placeholder="Optional"
                                    ></b-form-input>
                                    <label class="ml-1">Hour(s)</label>
                                </div>

                                <div class="d-flex flex-column">
                                    <b-form-input
                                        id="dueAfterStartTreatmentMinutes"
                                        ref="dueAfterStartTreatmentMinutes"
                                        v-model.number="dueInMinutes"
                                        v-validate="'required|numeric'"
                                        type="number"
                                        :class="{
                                            'border-danger': errors.has(
                                                'dueAfterStartTreatmentMinutes'
                                            )
                                        }"
                                        name="dueAfterStartTreatmentMinutes"
                                        class="form-control"
                                        placeholder="Required"
                                    ></b-form-input>
                                    <label class="ml-1">Minute(s)</label>
                                </div>
                            </div>

                            <!-- Datetime Picker -->
                            <vet-datetime
                                v-if="dueType === SPECIFICTIME"
                                v-model="dueDateTime"
                                v-validate="'required'"
                                :class="{
                                    'border-danger': errors.has(
                                        'dueSpecificTime'
                                    )
                                }"
                                class="mt-1"
                                id="dueSpecificTime"
                                ref="dueSpecificTime"
                                name="dueSpecificTime"
                                style="flex: auto;"
                                :placeholder="{
                                    date: 'Date (required)',
                                    time: 'Time (required)'
                                }"
                                :container="`#${parentRef}`"
                                :isUpdatePosition="isUpdatePosition"
                                rules="required"
                                format="DateTime"
                            ></vet-datetime>
                            <div
                                v-if="isParentItem"
                                :class="dueType === SPECIFICTIME ? 'mt-2' : ''"
                                class="alert alert-warning"
                                role="alert"
                            >
                                This is a recurring activity. When editing a
                                primary activity in the series, the due date and
                                time of subsequent linked activities in the
                                series will adjust accordingly.
                            </div>
                        </template>
                    </div>
                </b-form-checkbox>
            </b-col>
            <b-col v-if="element.medications" class="p-0" cols="4">
                <b-row no-gutters style="margin: -0.25rem 0;">
                    <b-form-input
                        v-if="isConvertEstimate"
                        type="number"
                        class="col-4"
                        @blur="quantityBlur"
                        :disabled="true"
                        v-model="computedQuantity"
                    ></b-form-input>

                    <b-form-input
                        v-else
                        type="number"
                        class="col-4"
                        @blur="quantityBlur"
                        :disabled="!element.selected"
                        v-model="element.medications[0].quantity"
                    ></b-form-input>
                    <span class="py-1 col-5">
                        {{ element.medications[0].uom.value }}
                    </span>
                    <dosage
                        v-if="weight"
                        :item-id="element.medications[0].item_id"
                        :weight-data="{
                            weight: weight.value,
                            uom: weight.uom_id
                        }"
                        :container="null"
                        :data="{ id: element.id }"
                        target="quantity"
                        parent-id="treatementStart"
                        class="btn-sm ml-auto"
                    />
                </b-row>
            </b-col>
        </b-row>
        <hr class="mb-0" />
    </div>
</template>
<script>
import Dosage from "@/module/commons/dosage.vue";
import { DateTime } from "luxon";
import { get, find, some, cloneDeep, orderBy } from "lodash";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import ScheduleHelper from "@/module/schedule/helper";

const AFTERSTARTTREATMENT = 1;
const SPECIFICTIME = 2;

export default {
    name: "ActivitiesTemplateItems",
    mixins: [ScheduleHelper],
    components: {
        Dosage,
        vetDatetime
    },
    props: {
        element: {
            type: Object,
            default() {
                return { selected: true };
            }
        },
        patientId: {
            type: String,
            required: true,
            default: null
        },
        templates: {
            type: Array
        },
        isConvertEstimate: {
            type: Boolean,
            default: false
        },
        parentRef: {
            type: String,
            default: "dueSpecificTime"
        },
        isUpdatePosition: {
            type: Boolean,
            default: false
        },
        useHighRange: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            changeDateTime: false,
            dueDateTime: 0,
            dueInDays: 0,
            dueInHours: 0,
            dueInMinutes: 15,
            dueType: 1,
            dues: [
                {
                    value: 1,
                    text: "Due: After start of treatment"
                },
                {
                    value: 2,
                    text: "Due: At specific time"
                }
            ],
            AFTERSTARTTREATMENT,
            SPECIFICTIME
        };
    },
    computed: {
        due_text() {
            let elementTime = DateTime.fromSeconds(this.element.time).setZone(
                this._timeZone
            );
            let time = elementTime.toFormat("hh:mm a");
            let date = elementTime.toFormat(this._dateFormat);

            return `${date} ${time}`;
        },
        weight() {
            const vitals = get(
                this.$store.state.patients,
                this.patientId + ".vitals"
            );
            let weight = find(vitals, { type: 4 });

            return vitals && weight ? weight : { value: null, uom_id: null };
        },
        totalDue() {
            let daysInMinutes = this.dueInDays * 24 * 60;
            let hoursInMinutes = this.dueInHours * 60;
            let minutes = this.dueInMinutes;
            return daysInMinutes + hoursInMinutes + minutes;
        },
        isParentItem() {
            return (
                this.element.repeat_data &&
                this.element.id === this.element.occurrence_id
            );
        },
        computedQuantity() {
            if (!this.element.selected || !this.isConvertEstimate) {
                return 0;
            }

            if (this.element.estimateItem) {
                if (this.element.estimateItem.quantity_max != -1) {
                    return this.useHighRange
                        ? this.element.estimateItem.quantity_max
                        : this.element.estimateItem.quantity_min;
                } else {
                    return this.element.estimateItem.quantity_min;
                }
            } else {
                return 0;
            }
        },
        displayQuantity() {
            if (!this.isConvertEstimate) {
                return null;
            }

            if (this.element.estimateItem) {
                const uom = this.element?.estimateItem?.item?.item_uom
                    ? this.element.estimateItem.item.item_uom.value
                    : "";
                if (
                    this.element.estimateItem.quantity_max > 0 &&
                    this.element.estimateItem.quantity_min !=
                        this.element.estimateItem.quantity_max
                ) {
                    return `( ${this.element.estimateItem.quantity_min} - ${this.element.estimateItem.quantity_max} ${uom} )`;
                }
                return `( ${this.element.estimateItem.quantity_min} ${uom} )`;
            }

            return null;
        }
    },
    watch: {
        computedQuantity: {
            handler(val) {
                if (this.isConvertEstimate && this.element.estimateItem) {
                    this.element.medications[0].quantity = val;
                }
            },
            immediate: true
        },
        "element.selected"(value) {
            this.$emit("selected", value);
            this.$emit("update-selected-all");
        },
        changeDateTime(isOpen) {
            if (isOpen) {
                // wait until the DOM is ready, then start listening
                this.$nextTick(
                    () =>
                        document.addEventListener(
                            "click",
                            this._onDocClick,
                            true
                        ) // capture phase
                );
            } else {
                document.removeEventListener("click", this._onDocClick, true);
                this.$nextTick(() => {
                    this.dueType = this.element.due_type;
                    this.dueDateTime = this.element.time;
                    if (this.dueType == AFTERSTARTTREATMENT) {
                        this.segregateDue(this.element.due);
                    }
                });
            }
        },
        element(newEle) {
            this.dueType = newEle.due_type;
            this.dueDateTime = newEle.time;

            if (this.dueType == AFTERSTARTTREATMENT) {
                this.segregateDue(newEle.due);
            }
        }
    },
    beforeDestroy() {
        document.removeEventListener("click", this._onDocClick, true);
    },
    mounted() {
        this.dueType = this.element.due_type;
        this.dueDateTime = this.element.time;

        if (this.dueType == AFTERSTARTTREATMENT) {
            this.segregateDue(this.element.due);
        }
    },
    methods: {
        segregateDue(due) {
            let dueInDays = Math.floor(due / (24 * 60));
            let dueInHours = Math.floor((due % (24 * 60)) / 60);
            let dueInMinutes = (due % (24 * 60)) % 60;

            this.dueInDays = dueInDays;
            this.dueInHours = dueInHours;
            this.dueInMinutes = dueInMinutes;
        },
        _onDocClick(evt) {
            evt.preventDefault();

            // 1️⃣  click is inside the row itself?
            const insideRow = this.$el.contains(evt.target);

            // 2️⃣  click is inside ANY part of the datetime or timepicker popup?
            const insidePopup = Boolean(
                evt.target.closest(
                    ".bootstrap-datetimepicker-widget, .bootstrap-timepicker-widget"
                )
            );

            // 3️⃣  if the click is outside both → close the editor
            if (!insideRow && !insidePopup) {
                this.changeDateTime = false;
            }
        },
        stateChanged(event) {
            if (some(this.element.medications)) {
                if (event) {
                    if (this.element.originalQuantity) {
                        this.element.medications[0].quantity = this.element.originalQuantity;
                    }
                } else {
                    this.element.originalQuantity = this.element.medications[0].quantity;
                    this.element.medications[0].quantity = 0;
                }
            }
        },
        quantityBlur(event) {
            if (event.target.value === "") {
                this.element.medications[0].quantity = "0";
            }
        },
        async updateDateTime() {
            let valid = await this.$validator.validateAll();
            if (!valid) return;

            /* ─────────────────── 1. setup ─────────────────── */
            const templates = cloneDeep(this.templates);
            const target = templates.find(t => t.id === this.element.id);

            // Which due-type are we using?
            const isAfterStart = this.dueType === AFTERSTARTTREATMENT;
            const DUE_TYPE = isAfterStart ? AFTERSTARTTREATMENT : SPECIFICTIME;

            // Base “due” for the current template (epoch-seconds)
            const baseDue = isAfterStart
                ? this.totalDue
                : this.dueDateTime instanceof DateTime
                ? this.dueDateTime.toSeconds()
                : this.dueDateTime;

            /* ─────────────────── helpers ─────────────────── */
            const calc = due =>
                this._calculateDueAlt(
                    this.element.start
                        ? DateTime.fromSeconds(this.element.start)
                        : this._now(),
                    due,
                    DUE_TYPE,
                    this._now()
                );

            const apply = (tpl, due) => {
                const { time } = calc(due);
                tpl.time = time;
                tpl.due = due;
                tpl.due_type = DUE_TYPE;
            };

            /* ─────────────────── 2. always update *this* template ─────────────────── */
            apply(target, baseDue);

            /* ─────────────────── 3. if it’s the recurrence parent, update children ─────────────────── */
            const isRecurrenceParent =
                this.element.repeat_data &&
                this.element.id === this.element.occurrence_id;

            if (isRecurrenceParent) {
                templates
                    .filter(
                        t =>
                            t.occurrence_id === this.element.occurrence_id &&
                            t.id !== this.element.id
                    )
                    .forEach((tpl, idx) => {
                        const incMinutes =
                            tpl.repeat_data.specific_interval * (idx + 1);
                        const due = isAfterStart
                            ? baseDue + incMinutes // units already match
                            : baseDue + incMinutes * 60; // minutes → seconds
                        apply(tpl, due);
                    });
            }

            /* ─────────────────── 4. notify parent ─────────────────── */
            this.$emit(
                "update-new-templates",
                orderBy(templates, "time", "asc")
            );

            this.$nextTick(() => {
                this._message("Date and time updated and sorted");
                this.changeDateTime = false;
            });
        }
    }
};
</script>
